import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Shield, ShieldOff, Save, RefreshCw } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent } from "../../components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SafeModePanel({ token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [state, setState] = useState({ enabled: false, phone: "", email: "" });
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/safe-mode`, { headers });
      setState(r.data);
      setDraft({});
    } catch (e) {
      toast.error("Failed to load SAFE-MODE state");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async () => {
    const next = !state.enabled;
    if (next && !window.confirm(
      "Turn SAFE-MODE ON?\n\nAll outbound SMS / email will be REDIRECTED to:\n" +
      `  • Phone: ${draft.phone ?? state.phone}\n` +
      `  • Email: ${draft.email ?? state.email}\n\n` +
      "User-facing send buttons will return a 423 error until you turn this off."
    )) return;
    if (!next && !window.confirm(
      "Turn SAFE-MODE OFF?\n\nReal SMS and emails will go to real recipients.\n\n" +
      "Make sure the season is active and you actually want messages to flow before confirming."
    )) return;

    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/safe-mode`,
        { enabled: next, phone: draft.phone ?? state.phone, email: draft.email ?? state.email },
        { headers },
      );
      setState(r.data);
      setDraft({});
      toast.success(next ? "SAFE-MODE is now ON" : "SAFE-MODE is now OFF");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toggle failed");
    }
    setSaving(false);
  };

  const handleSaveRedirect = async () => {
    if (!draft.phone && !draft.email) return;
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/safe-mode`,
        { enabled: state.enabled, ...(draft.phone && { phone: draft.phone }), ...(draft.email && { email: draft.email }) },
        { headers },
      );
      setState(r.data);
      setDraft({});
      toast.success("Redirect target saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  if (loading) {
    return <Card><CardContent className="py-3 px-4 text-xs text-muted-foreground">Loading SAFE-MODE…</CardContent></Card>;
  }

  const dirty = Object.keys(draft).length > 0;
  const Icon = state.enabled ? Shield : ShieldOff;

  return (
    <Card className={state.enabled ? "border-2 border-amber-500 bg-amber-50/60" : "border-stone-200"} data-testid="admin-safe-mode-panel">
      <CardContent className="py-3 px-4 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Icon className={`w-5 h-5 ${state.enabled ? "text-amber-700" : "text-stone-400"}`} />
            <div>
              <div className="text-sm font-bold">
                SAFE-MODE:{" "}
                <span className={state.enabled ? "text-amber-700" : "text-stone-500"} data-testid="safe-mode-status">
                  {state.enabled ? "ON" : "OFF"}
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {state.enabled
                  ? `All outbound SMS → ${state.phone} · email → ${state.email}. Send buttons return 423.`
                  : "Real SMS/email will go to real recipients. Turn ON before off-season / archive."}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={load} title="Refresh" data-testid="safe-mode-refresh">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button
              onClick={handleToggle}
              disabled={saving}
              size="sm"
              className={state.enabled
                ? "bg-stone-600 hover:bg-stone-700 text-white"
                : "bg-amber-600 hover:bg-amber-700 text-white"}
              data-testid="safe-mode-toggle"
            >
              {saving ? "…" : (state.enabled ? "Turn OFF" : "Turn ON")}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1 border-t">
          <div>
            <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Redirect Phone</label>
            <Input
              value={draft.phone ?? state.phone}
              onChange={e => setDraft(d => ({ ...d, phone: e.target.value }))}
              placeholder="+15163501454"
              className="h-8 text-xs"
              data-testid="safe-mode-phone-input"
            />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Redirect Email</label>
            <Input
              value={draft.email ?? state.email}
              onChange={e => setDraft(d => ({ ...d, email: e.target.value }))}
              placeholder="community@rollingriver.com"
              className="h-8 text-xs"
              data-testid="safe-mode-email-input"
            />
          </div>
          {dirty && (
            <div className="sm:col-span-2">
              <Button size="sm" variant="outline" onClick={handleSaveRedirect} disabled={saving} className="h-7 text-xs" data-testid="safe-mode-save-redirect">
                <Save className="w-3 h-3 mr-1" /> Save redirect target
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
